import type { Signee } from '@/types/Signee.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import { useApi } from '@/composables/useApi.ts';

export function useLocalInviteesDataActions(store) {
  const createStore = store;
  const { deepSignAPI } = useApi();

  function addInviteeToLocalData(invitee: Signee) {
    if (createStore.signeesOrdered.length === 0) {
      createStore.signeesOrdered = [[]];
    }
    if (createStore.ordered) {
      if (!createStore.hasInvitees) {
        createStore.signeesOrdered = [];
      }
      createStore.signeesOrdered.push([invitee]);
    } else {
      if (createStore.signeesOrdered.length === 1) {
        createStore.signeesOrdered[0].push(invitee);
      }
    }
  }

  function updateSignOrderInLocalData(groups: Array<Array<Signee>>) {
    groups.forEach((group, groupIndex) => {
      group.forEach((invitee: Signee) => {
        invitee.signOrder = groupIndex;
      });

      // We need to normalize the in-group order here as the BE ignores the
      // in-group position of an invitee. Approvers must ALWAYS come first within
      // a group (they can never sit between signees); the rest keeps the
      // alphabetical-by-email ordering.
      group.sort((a, b) => {
        if (a.signeeType !== b.signeeType) {
          return a.signeeType === SigneeType.APPROVE ? -1 : 1;
        }
        return (a.email ?? '').localeCompare(b.email ?? '');
      });
    });
    createStore.signeesOrdered = groups;
  }

  function updateInviteeInLocalData(invitee: Signee) {
    createStore.signeesOrdered.forEach((group) => {
      const match = group.find(
        (_invitee) => _invitee.signeeId === invitee.signeeId
      );
      if (match) {
        match.email = invitee.email;
        match.autographPosition = invitee.autographPosition;
        match.signings = invitee.signings;
        match.signOrder = invitee.signOrder;
      }
    });

    // `document.signeeGroups` is COMMITTED document data — the placement rebuild
    // and the drawer's per-file badges read it — so the signing-workflow dialog
    // must not write into it: while it defers, its edits belong in the draft
    // only and reach the document through reconcile on Confirm (which then
    // re-fetches the groups from the backend anyway).
    if (createStore.deferPersistence) return;

    // `signeesOrdered` is a flattened view; the authoritative per-signee data
    // (notably `signings`, read by resolveSigneeSignings and the placement
    // rebuild) lives on `document.signeeGroups[].signees[]`. Mirror the update
    // there too so the placed/removed placeholders reflect immediately without a
    // page reload. Reuse the fresh signee returned by the update-signee endpoint.
    createStore.document?.signeeGroups?.forEach(
      (group: { signees?: Signee[] }) => {
        const match = group.signees?.find(
          (_invitee) => _invitee.signeeId === invitee.signeeId
        );
        if (match) {
          match.email = invitee.email;
          match.autographPosition = invitee.autographPosition;
          match.signings = invitee.signings;
          match.signOrder = invitee.signOrder;
          match.policy = invitee.policy;
        }
      }
    );
  }

  function createInitialGroupsWithApprovers(groups: Array<Array<Signee>>) {
    const approverGroup: Array<Signee> = [];
    const signeeGroup: Array<Signee> = [];
    groups.forEach((group) => {
      if (group.length > 0) {
        group.forEach((invitee: Signee) => {
          if (invitee.signeeType === SigneeType.SIGN) {
            signeeGroup.push(invitee);
          } else {
            approverGroup.push(invitee);
          }
        });
      }
    });
    return [approverGroup, signeeGroup];
  }

  function createUnorderedGroup(groups: Array<Array<Signee>>) {
    // Collapsing into a single (unordered) group must also reset each signee's
    // signOrder back to 0 — otherwise a signee that was previously assigned a
    // later group index (e.g. while the order switch was ON) keeps that stale
    // value. That leaks into the create-signee payload for any signee added
    // afterwards while unordered (reconcileParticipants sends
    // `signOrder: signee.signOrder || 0`), wrongly creating it with a non-zero
    // order even though there are no groups.
    const flatSignees = groups.flat();
    flatSignees.forEach((signee: Signee) => {
      signee.signOrder = 0;
    });
    return [flatSignees];
  }

  function removeInviteeFromLocalData(id: string): Promise<void> {
    return new Promise(async (resolve) => {
      const groups = createStore.signeesOrdered.map(
        (inviteeGroup: Array<Signee>) => {
          return inviteeGroup
            .flat()
            .filter((invitee: Signee) => invitee.signeeId !== id);
        }
      );
      const updatedGroups = filterEmptyGroups(groups);

      // explicitly update order (skipped while persistence is deferred — the
      // signing-workflow dialog persists the final order on Confirm)
      if (createStore.ordered && !createStore.deferPersistence) {
        const data = createStore.createUpdateSignOrderPayload(updatedGroups);
        await deepSignAPI.value.documentsSignOrder.update(
          createStore.document.documentId,
          data
        );
      }
      updateSignOrderInLocalData(updatedGroups);
      resolve();
    });
  }

  function filterEmptyGroups(groups: Array<Array<Signee>>) {
    return groups.filter((group) => group.length > 0);
  }

  function filterEmptyInBetweenGroups(groups: Array<Array<Signee>>) {
    return groups.filter((group, index) => {
      if (index === 0) {
        return true;
      }

      if (index === groups.length - 1) {
        return true;
      }

      return group.length > 0;
    });
  }

  function filterInviteeFromLocalData(inviteeToRemove: Signee) {
    return createStore.signeesOrdered.map((group) =>
      group.filter(
        (invitee: Signee) => invitee.signeeId !== inviteeToRemove.signeeId
      )
    );
  }

  function clearInvalidFields() {
    createStore.signeesOrdered.forEach((signeeGroup: Array<Signee>) => {
      signeeGroup.forEach((invitee: Signee) => {
        if (!createStore.validateEmail(invitee.email)) {
          invitee.email = '';
        }
      });
    });
  }

  function addNewFirstGroupAsDropZone() {
    createStore.signeesOrdered.splice(0, 0, []);
  }

  function addNewLastGroupAsDropZone() {
    createStore.signeesOrdered.push([]);
  }

  function setSigneeAttachmentsAllowed(value: boolean) {
    createStore.document.signeeAttachmentsAllowed = value;
  }

  function setEnforceAuthorityService(serviceKey: string) {
    createStore.document.requiredAuthorityService = serviceKey;
  }

  function addDropZones(groups: Array<Array<Signee>> | null) {
    const inviteeGroups = groups ? groups : createStore.signeesOrdered;
    const isFirstGroupEmpty = inviteeGroups[0].length === 0;
    const isLastGroupEmpty =
      inviteeGroups[inviteeGroups.length - 1].length === 0;
    if (isFirstGroupEmpty && isLastGroupEmpty) {
      return;
    }
    if (!isFirstGroupEmpty) {
      addNewFirstGroupAsDropZone();
    }

    if (!isLastGroupEmpty) {
      addNewLastGroupAsDropZone();
    }
  }

  return {
    addDropZones,
    addInviteeToLocalData,
    clearInvalidFields,
    createInitialGroupsWithApprovers,
    createUnorderedGroup,
    filterEmptyInBetweenGroups,
    filterEmptyGroups,
    filterInviteeFromLocalData,
    updateSignOrderInLocalData,
    updateInviteeInLocalData,
    removeInviteeFromLocalData,
    setSigneeAttachmentsAllowed,
    setEnforceAuthorityService
  };
}
